<template>
  <div v-if="data" class="relative h-full pb-4 overflow-x-auto bg-white">
    <table v-if="data" class="w-full text-sm text-left text-gray-900 divide-y divide-gray-200">
      <tbody>
        <BaseDetailsTableRow
          :name="$t('representative.customerCode')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            v-if="data.companyCustomer"
            :mode="mode"
            v-model="data.companyCustomer.code"
            color="base"
            flat
            :warning="getFieldSoftError(['representativeCustomerId', 'companyCustomerId'])"
            show-error-as-tooltip
            @click="isCustomerModalOpen = true" />
          <BaseInput
            v-else
            :mode="mode"
            color="base"
            flat
            :disabled="!data.representativeCustomerCode"
            :warning="getFieldSoftError(['representativeCustomerId', 'companyCustomerId'])"
            show-error-as-tooltip
            @click="isCustomerModalOpen = true" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('representative.customerRemotedCode')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="'view'"
            v-model="data.representativeCustomerCode"
            color="base"
            flat
            :warning="getFieldSoftError(['representativeCustomerCode'])"
            show-error-as-tooltip />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('representative.customerRemotedTaxCode')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="'view'"
            v-model="data.representativeCustomerTaxCode"
            color="base"
            flat
            :warning="getFieldSoftError(['representativeCustomerTaxCode'])"
            show-error-as-tooltip />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('representative.customerRemotedName')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="'view'"
            v-model="data.representativeCustomerName"
            color="base"
            flat
            :warning="getFieldSoftError(['representativeCustomerName'])"
            show-error-as-tooltip />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('representative.customerRemotedAddress')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="'view'"
            v-model="data.representativeCustomerAddress"
            color="base"
            flat
            :warning="getFieldSoftError(['representativeCustomerAddress'])"
            show-error-as-tooltip />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('representative.customerRemotedCity')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="'view'"
            v-model="data.representativeCustomerCity"
            color="base"
            flat
            :warning="getFieldSoftError(['representativeCustomerCity'])"
            show-error-as-tooltip />
        </BaseDetailsTableRow>

        <!-- Customer Site Information -->

        <BaseDetailsTableRow
          :name="$t('representative.customerSiteCode')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.customerSiteCode"
            color="base"
            flat
            :warning="getFieldSoftError(['customerSiteCode'])"
            show-error-as-tooltip />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('representative.customerSiteName')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.customerSiteName"
            color="base"
            flat
            :warning="getFieldSoftError(['customerSiteName'])"
            show-error-as-tooltip />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('representative.customerSiteAddress')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.customerSiteAddress"
            color="base"
            flat
            :warning="getFieldSoftError(['customerSiteAddress'])"
            show-error-as-tooltip />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('representative.customerSiteCity')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.customerSiteCity"
            color="base"
            flat
            :warning="getFieldSoftError(['customerSiteCity'])"
            show-error-as-tooltip />
        </BaseDetailsTableRow>
      </tbody>
    </table>

    <DCCustomerModalCreateEdit
      v-if="data.representativeCustomer && isCustomerModalOpen"
      :customer="data.representativeCustomer"
      :representative-id="data.representativeId!"
      :existing-customers="representativeCustomers"
      mode="assign"
      @save="onDCCustomerSave"
      @close-modal="isCustomerModalOpen = false" />
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import BaseDetailsTableRow from '@/components/common/BaseDetailsTableRow.vue';
import BaseInput from '../common/BaseInput.vue';
import DCCustomerModalCreateEdit from '@/components/traders/DCCustomerModalCreateEdit.vue';
import type { DCCustomer, DCDocumentHeader } from '@/types/DataCollection';
import { ref, onMounted } from 'vue';
import { getDCCustomersWithConditions } from '@/services/api/dataCollection';

const { t: $t } = useI18n();

const props = defineProps<{
  mode: 'view' | 'edit';
  isHorizontalLayout?: boolean;
  validations?: any;
}>();

const data = defineModel<Partial<DCDocumentHeader> | null>();

const representativeCustomers = ref<DCCustomer[]>([]);
const isCustomerModalOpen = ref(false);

onMounted(() => {
  if (data.value?.representativeId) {
    loadRepresentativeCustomers(data.value.representativeId);
  }
});

async function loadRepresentativeCustomers(representativeId: string) {
  try {
    const customers = await getDCCustomersWithConditions({ representativeId });
    representativeCustomers.value = customers.data || [];
  } catch (error) {
    console.error('Failed to load representative customers:', error);
    representativeCustomers.value = [];
  }
}

function onDCCustomerSave(customer: DCCustomer) {
  if (!data.value) return;
  data.value.representativeCustomer = customer;
  data.value.companyCustomerId = customer.customerId;
  data.value.companyCustomer = customer.customer;
  isCustomerModalOpen.value = false;
}

function getFieldSoftError(fieldNames: string[]): string {
  if (!props.validations || props.mode === 'view') return '';

  for (const fieldName of fieldNames) {
    const validationField = props.validations[fieldName];
    if (validationField?.dynamicRequired?.$invalid) {
      return validationField.dynamicRequired.$message;
    }
  }

  return '';
}
</script>
